import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from '../hooks/useScrollLock';
import { X, ShoppingCart, Zap } from 'lucide-react';
import { Product } from '../types';
import { formatCurrencyNPR } from '../utils/currency';
import { getAttr } from '../utils/variant';
import { getProductPrice, getProductMRP } from '../utils/productUtils';
import { sanitizeString } from '../utils/sanitization';

interface ProductPreferencesModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (quantity: number, selectedSize?: string, selectedColor?: string) => void;
  onBuyNow: (quantity: number, selectedSize?: string, selectedColor?: string) => void;
}

const ProductPreferencesModal: React.FC<ProductPreferencesModalProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  onBuyNow
}) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  // Get available sizes and colors from variants
  const { availableSizes, availableColors, currentVariant } = useMemo(() => {
    // Check if variants exist and is an array
    if (!product?.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      // For products without variants, create a default variant
      // Use discounted price for default variant (getProductPrice handles discountPrice priority)
      const defaultPrice = getProductPrice(product);
      const defaultVariant = {
        id: 'default',
        attributeValues: { Size: 'One Size', Color: 'Default' },
        stockQty: product?.totalStock || 0,
        priceOverride: defaultPrice, // Use discounted price, not original price
        sku: `${product?.id}_OneSize_Default`,
        soldQuantity: 0
      };
      return { 
        availableSizes: ['One Size'], 
        availableColors: ['Default'], 
        currentVariant: defaultVariant 
      };
    }

    // For products with variants - ensure we have a valid array
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const sizes = [...new Set(variants.map(v => getAttr(v, "Size")).filter(Boolean))];
    const colors = [...new Set(variants.map(v => getAttr(v, "Color")).filter(Boolean))];
    
    // Debug info removed for production
    
    // Find current variant based on selected size and color
    const currentVariant = variants.find(v => {
      const variantSize = getAttr(v, "Size") || 'One Size';
      const variantColor = getAttr(v, "Color") || 'Default';
      return variantSize === selectedSize && variantColor === selectedColor;
    });

    return { 
      availableSizes: sizes, 
      availableColors: colors, 
      currentVariant 
    };
  }, [product, selectedSize, selectedColor]);

  // Set default selections when modal opens, but do NOT override user choice
  React.useEffect(() => {
    if (!isOpen) return;
    if (!selectedSize && availableSizes.length > 0) {
      setSelectedSize(availableSizes[0] || '');
    }
    if (!selectedColor && availableColors.length > 0) {
      setSelectedColor(availableColors[0] || '');
    }
  }, [isOpen, availableSizes, availableColors, selectedSize, selectedColor]);

  // Calculate price using centralized function (considers selected variant)
  // Always uses discounted price - getProductPrice prioritizes discountPrice over price
  const priceCalculations = useMemo(() => {
    if (!product) {
      return {
        currentPrice: 0,
        originalPrice: 0,
        totalPrice: 0,
        totalOriginalPrice: 0,
        savings: 0,
        savingsPercentage: 0,
      };
    }

    // For simple products (no variants), always use getProductPrice without variation
    // For variable products, use selected variation if available
    // IMPORTANT: Only pass selectedVariation if both size and color are selected AND product has variants
    // Otherwise, getProductPrice will use discountPrice for simple products
    const hasVariants = product.variants && Array.isArray(product.variants) && product.variants.length > 0;
    const selectedVariation = (hasVariants && selectedSize && selectedColor)
      ? { size: selectedSize, color: selectedColor }
      : undefined;
    
    // CRITICAL FIX: Always use discounted price
    // Priority: product.discountPrice > variant-specific discount > variant price > product.price
    let currentPrice: number;
    
    // First, always check if product has discountPrice (this is the main discounted price)
    if (typeof product.discountPrice === 'number' && product.discountPrice > 0) {
      // Product has discountPrice - use it unless variant has its own discounted price
      if (hasVariants && selectedVariation && currentVariant) {
        const variantPrice = (currentVariant as any)?.price ?? currentVariant?.priceOverride;
        // Only use variant price if it's different from original AND different from discountPrice
        // (meaning variant has its own specific discount)
        if (typeof variantPrice === 'number' && variantPrice >= 0 && 
            variantPrice !== product.price && variantPrice !== product.discountPrice) {
          currentPrice = variantPrice; // Variant has unique discounted price
        } else {
          currentPrice = product.discountPrice; // Use product discountPrice
        }
      } else {
        currentPrice = product.discountPrice; // Simple product or no variant selected
      }
    } else {
      // No product discountPrice, use getProductPrice which handles variant prices
      currentPrice = getProductPrice(product, selectedVariation);
    }
    // getProductMRP always returns original price (price field)
    const originalPrice = getProductMRP(product);
    const totalPrice = currentPrice * quantity;
    const totalOriginalPrice = originalPrice * quantity;
    const savings = Math.max(0, totalOriginalPrice - totalPrice);
    const savingsPercentage = originalPrice > 0 ? ((originalPrice - currentPrice) / originalPrice) * 100 : 0;
    
    return {
      currentPrice,
      originalPrice,
      totalPrice,
      totalOriginalPrice,
      savings,
      savingsPercentage,
    };
  }, [product, quantity, selectedSize, selectedColor]);

  const handleSizeChange = (size: string) => {
    // SECURITY: Sanitize size before setting state
    const sanitizedSize = sanitizeString(size, 50) || '';
    setSelectedSize(sanitizedSize);
    // Reset color if current color is not available for new size
    if (selectedColor && !availableColors.includes(selectedColor)) {
      const firstColor = availableColors[0];
      if (firstColor) {
        const sanitizedColor = sanitizeString(firstColor, 50) || '';
        setSelectedColor(sanitizedColor);
      }
    }
  };

  const handleColorChange = (color: string) => {
    // SECURITY: Sanitize color before setting state
    const sanitizedColor = sanitizeString(color, 50) || '';
    setSelectedColor(sanitizedColor);
    // Reset size if current size is not available for new color
    if (selectedSize && !availableSizes.includes(selectedSize)) {
      const firstSize = availableSizes[0];
      if (firstSize) {
        const sanitizedSize = sanitizeString(firstSize, 50) || '';
        setSelectedSize(sanitizedSize);
      }
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSize || !selectedColor) {
      return;
    }

    // SECURITY: Sanitize variant attributes before passing to callback
    const sanitizedSize = sanitizeString(selectedSize, 50) || '';
    const sanitizedColor = sanitizeString(selectedColor, 50) || '';
    
    if (!sanitizedSize || !sanitizedColor) {
      return;
    }

    if (currentVariant && quantity > (currentVariant.stockQty ?? 0)) {
      return;
    }

    setLoading(true);
    try {
      await onAddToCart(quantity, sanitizedSize, sanitizedColor);
      onClose();
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = async () => {
    if (!selectedSize || !selectedColor) {
      return;
    }

    // SECURITY: Sanitize variant attributes before passing to callback
    const sanitizedSize = sanitizeString(selectedSize, 50) || '';
    const sanitizedColor = sanitizeString(selectedColor, 50) || '';
    
    if (!sanitizedSize || !sanitizedColor) {
      return;
    }

    if (currentVariant && quantity > (currentVariant.stockQty ?? 0)) {
      return;
    }

    setLoading(true);
    try {
      await onBuyNow(quantity, sanitizedSize, sanitizedColor);
      onClose();
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
    } finally {
      setLoading(false);
    }
  };

  // Enhanced disabled logic with better stock validation
  const isDisabled = useMemo(() => {
    if (loading) return true;
    
    // Check if size/color selections are missing
    if (availableSizes.length > 0 && !selectedSize) return true;
    if (availableColors.length > 0 && !selectedColor) return true;
    
    // Check stock availability
    if (currentVariant) {
      const stock = currentVariant.stockQty ?? 0;
      return stock === 0 || quantity > stock;
    } else {
      // Fallback to product total stock
      const totalStock = product?.totalStock ?? 0;
      return totalStock === 0 || quantity > totalStock;
    }
  }, [loading, availableSizes.length, selectedSize, availableColors.length, selectedColor, currentVariant, quantity, product?.totalStock]);

  // Lock body scroll while modal is open
  useScrollLock(isOpen);

  if (!isOpen) return null;

  const modal = (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="fixed inset-0 flex items-center justify-center z-[9999] p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Select Preferences</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Product Info */}
          <div className="p-4 border-b border-gray-100">
           <div className="flex items-center space-x-3">
             <img
               src={product?.images?.[0] || '/placeholder-product.jpg'}
               alt={product?.title || 'Product'}
               className="w-12 h-12 object-cover rounded-lg"
               loading="lazy"
               decoding="async"
               width={48}
               height={48}
             />
             <div className="flex-1 min-w-0">
               <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                 {product?.title || 'Product'}
               </h4>
               <div className="flex items-center space-x-2 mt-1">
                 <span className="text-base font-bold text-gray-900">
                   {formatCurrencyNPR(priceCalculations.currentPrice)}
                 </span>
                 {priceCalculations.savingsPercentage > 0 && (
                   <span className="text-xs text-gray-500 line-through">
                     {formatCurrencyNPR(priceCalculations.originalPrice)}
                   </span>
                 )}
               </div>
             </div>
           </div>
         </div>

                 {/* Preferences Form */}
         <div className="p-4 space-y-4">
                     {/* Size Selection */}
           {availableSizes.length > 0 && (
             <div>
               <label className="block text-xs font-medium text-gray-700 mb-2">
                 Size
               </label>
               <div className="grid grid-cols-3 gap-1.5">
                                 {availableSizes.map((size) => {
                   // SECURITY: Sanitize size before rendering and using
                   const sanitizedSize = sanitizeString(size, 50) || '';
                   if (!sanitizedSize) return null;
                   
                   return (
                     <button
                       key={sanitizedSize}
                       type="button"
                       onClick={(e) => {
                         e.preventDefault();
                         e.stopPropagation();
                         handleSizeChange(sanitizedSize);
                       }}
                       className={`px-2 py-1.5 text-xs border rounded-md transition-colors ${
                         selectedSize === sanitizedSize
                           ? 'border-red-500 bg-red-50 text-red-700'
                           : 'border-gray-300 hover:border-gray-400'
                       }`}
                     >
                       {/* SECURITY: Sanitized size is safe to render */}
                       {sanitizedSize}
                     </button>
                   );
                 })}
              </div>
            </div>
          )}

                     {/* Color Selection */}
           {availableColors.length > 0 && (
             <div>
               <label className="block text-xs font-medium text-gray-700 mb-2">
                 Color
               </label>
               <div className="grid grid-cols-3 gap-1.5">
                                 {availableColors.map((color) => {
                   // SECURITY: Sanitize color before rendering and using
                   const sanitizedColor = sanitizeString(color, 50) || '';
                   if (!sanitizedColor) return null;
                   
                   return (
                     <button
                       key={sanitizedColor}
                       type="button"
                       onClick={(e) => {
                         e.preventDefault();
                         e.stopPropagation();
                         handleColorChange(sanitizedColor);
                       }}
                       className={`px-2 py-1.5 text-xs border rounded-md transition-colors ${
                         selectedColor === sanitizedColor
                           ? 'border-red-500 bg-red-50 text-red-700'
                           : 'border-gray-300 hover:border-gray-400'
                       }`}
                     >
                       {/* SECURITY: Sanitized color is safe to render */}
                       {sanitizedColor}
                     </button>
                   );
                 })}
              </div>
            </div>
          )}

                     {/* Quantity */}
           <div>
             <label className="block text-xs font-medium text-gray-700 mb-2">
               Quantity
             </label>
             <div className="flex items-center space-x-2">
                             <button
                 onClick={() => setQuantity(Math.max(1, quantity - 1))}
                 className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
               >
                 -
               </button>
               <span className="w-8 text-center font-medium text-sm">{quantity}</span>
               <button
                 onClick={() => setQuantity(quantity + 1)}
                 className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
               >
                 +
               </button>
            </div>
                         {currentVariant && (
               <p className={`text-xs mt-1 ${
                 (currentVariant.stockQty ?? 0) === 0 
                   ? 'text-red-500 font-medium' 
                   : 'text-gray-500'
               }`}>
                 {currentVariant.stockQty ?? 0} available
                 {(currentVariant.stockQty ?? 0) === 0 && ' - Out of Stock'}
               </p>
             )}
          </div>

                     {/* Total Price */}
           <div className="bg-gray-50 rounded-md p-3">
             <div className="flex justify-between items-center">
               <span className="text-xs text-gray-600">Total:</span>
               <span className="text-base font-bold text-gray-900">
                 {formatCurrencyNPR(priceCalculations.totalPrice)}
               </span>
             </div>
             {priceCalculations.savings > 0 && (
               <div className="flex justify-between items-center mt-1">
                 <span className="text-xs text-green-600">You save:</span>
                 <span className="text-xs font-medium text-green-600">
                   {formatCurrencyNPR(priceCalculations.savings)}
                 </span>
               </div>
             )}
           </div>
        </div>

                 {/* Action Buttons */}
         <div className="p-4 border-t border-gray-100 space-y-2">
           <button
             onClick={handleBuyNow}
             disabled={isDisabled}
             className={`w-full font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm ${
               isDisabled && currentVariant && (currentVariant.stockQty ?? 0) === 0
                 ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                 : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
             }`}
           >
             <Zap className="w-4 h-4" />
             <span>
               {isDisabled && currentVariant && (currentVariant.stockQty ?? 0) === 0 
                 ? 'Out of Stock' 
                 : 'Buy Now'
               }
             </span>
           </button>
           
           <button
             onClick={handleAddToCart}
             disabled={isDisabled}
             className={`w-full font-medium py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm ${
               isDisabled && currentVariant && (currentVariant.stockQty ?? 0) === 0
                 ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                 : 'bg-gray-900 hover:bg-gray-800 text-white'
             }`}
           >
             <ShoppingCart className="w-4 h-4" />
             <span>
               {isDisabled && currentVariant && (currentVariant.stockQty ?? 0) === 0 
                 ? 'Out of Stock' 
                 : 'Add to Cart'
               }
             </span>
           </button>
         </div>
        </div>
      </div>
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default ProductPreferencesModal;
